import _ from 'underscore';
import { aggregatedRowsPlugin } from '@bingads-webui/projection-grid-aggregated-rows';

const maxRowsBeforeShowOverallTotalsOnTop = 10;

const getRawConfig = (i18n, permissions, {
  showSearchRow = true,
  showAudienceRow = true,
  showNativeRow = true,
  showDeletedRow = true,
  showPmaxRow = false,
  showLinkedInRow = false,
} = {}) => [{
  type: 'filtered',
  dataKey: '@ns.filtered.totals',
  number: '@odata.count',
}, {
  type: 'waterfallFilter',
  dataKey: '@ns.filtered.totals',
  number: '@odata.count',
}, {
  type: 'search',
  dataKey: '@ns.search.non.deleted.totals',
  text: i18n.getString('Summary_SearchTotal'),
  helpId: 'pop_BA_CampaignGrids_SearchTotal',
  ignore: !showSearchRow,
}, {
  type: 'audience',
  dataKey: '@ns.audience.non.deleted.totals',
  text: i18n.getString('Summary_AudienceTotal'),
  helpId: 'pop_BA_CampaignGrids_AudienceTotal',
  ignore: !showAudienceRow || !(permissions.IsAIMCampaignEnabled ||
    (permissions.IsNativeAdsAllowed && permissions.IsNativeAdsRebrandingEnabled)),
}, {
  type: 'pmax',
  dataKey: '@ns.pmax.non.deleted.totals',
  text: i18n.getString('Summary_PMaxTotal'),
  // helpId: 'pop_BA_CampaignGrids_PMaxTotal', // TODO: waiting for help id
  ignore: !showPmaxRow || !(permissions.dynamic.PerformanceMaxCampaigns && permissions.dynamic.PerformanceMaxCampaignsPhaseTwoCrossNetwork),
}, {
  type: 'native',
  dataKey: '@ns.native.non.deleted.totals',
  text: i18n.getString('Summary_NativeTotal'),
  ignore: !showNativeRow || !permissions.IsNativeAdsAllowed
  || permissions.IsNativeAdsRebrandingEnabled,
}, {
  type: 'linkedin',
  dataKey: '@ns.linkedin.non.deleted.totals',
  text: i18n.getString('Summary_LinkedInTotal'),
  helpId: 'pop_BA_LinkedIn_AdsTotal',
  ignore: !showLinkedInRow || !_.get(permissions, 'dynamic.LinkedInCampaigns', false),
}, {
  type: 'deleted',
  dataKey: '@ns.deleted.totals',
  text: i18n.getString('Summary_DeletedTotal'),
  helpId: 'pop_BA_CampaignGrids_DeletedTotal',
  ignore: !showDeletedRow,
}, {
  type: 'unknownDevices',
  dataKey: '@ns.unknown.totals',
}, {
  type: 'other',
  dataKey: '@ns.other.totals',
}, {
  type: 'overall',
  dataKey: '@ns.unfiltered.totals',
  number: '@ns.unfiltered.count',
}];

function getTotalRow(rawData, {
  type, dataKey, text, helpId, ignore,
}) {
  const data = rawData && rawData[dataKey];
  if (_.isUndefined(text)) {
    return null;
  }

  return !ignore ? {
    item: _.defaults({
      $total: { type, text, helpId },
    }, data),
    classes: `${type}-total-row`,
    type: `${type}-total-row`,
  } : null;
}

function cookRawConfig(config, rawData = {}, keys, i18n, helpIds) {
  return _.map(config, (cfg) => {
    const i18nKey = `${cfg.type}I18nKey`;
    const helpIdKey = `${cfg.type}HelpId`;
    if (cfg.number) {
      return _.defaults({
        text: (keys[i18nKey] && rawData[cfg.number] !== undefined) ? i18n.getString(keys[i18nKey], {
          number: i18n.formatDecimalToInteger(rawData[cfg.number]),
        }) : null,
        helpId: helpIds ? helpIds[helpIdKey] : cfg.helpId,
      }, cfg);
    }

    return _.has(keys, i18nKey)
      ? _.defaults({
        text: i18n.getString(keys[i18nKey]),
        helpId: helpIds ? helpIds[helpIdKey] : cfg.helpId,
      }, cfg)
      : cfg;
  });
}

function hasOnlyDataSourceFilters(filterService) {
  if (filterService &&
    _.isFunction(filterService.getCombinedFilter) &&
    _.isFunction(filterService.getDataSourceFilter)) {
    const combinedFilter = filterService.getCombinedFilter();
    const dataSourceFilter = _.result(filterService.getDataSourceFilter(), 'value', {});

    // All filter entities are data source filters
    if (!_.isNull(combinedFilter) && _.isEqual(combinedFilter.query, dataSourceFilter.query)) {
      return true;
    }
  }

  return false;
}

// {query: {Status: {$in: null}}} means no quick filter applied. We need to get rid of it.
// TODO: This is a tempory solution, Siming to remove this type of filter.
function isEmptyFilter(combinedFilter) {
  if (!combinedFilter
      || !combinedFilter.depth
      || combinedFilter.depth === 0) {
    return true;
  }

  if (combinedFilter.depth === 1) {
    if (!combinedFilter.query) {
      return true;
    }

    if (!combinedFilter.query.Status
        || !_.isEmpty(combinedFilter.query.Status.$in)) {
      return false;
    }

    return true;
  }

  return false;
}

export function aggregatedRowsPluginBuilder({
  keys,
  i18n,
  helpIds,
  permissions,
  filterService,
  customConfig,
  processBuilderConfig = cfg => cfg,
  processPluginConfig = cfg => cfg,
}) {
  const rawConfig = processBuilderConfig(getRawConfig(i18n, permissions, customConfig));
  const getUIFilters = () => {
    if (hasOnlyDataSourceFilters(filterService)) {
      return null; // We do not show data source filter entities on UI
    }

    if (filterService && _.isFunction(filterService.getCombinedFilter)) {
      const combinedFilter = filterService.getCombinedFilter();
      return isEmptyFilter(combinedFilter) ? null : combinedFilter.query;
    }

    return null;
  };

  // we show waterfall filter when waterfall filter is the only filter applied
  const showWaterFallFilterTotal = () => {
    if (!filterService) {
      return false;
    }

    const qFilter = filterService.getQuickFilter();

    // if there is any other type of filter, we should not show waterfall filter total.
    const combinedFilter = filterService.getCombinedFilter();

    return !_.isNull(qFilter) && !_.isNull(combinedFilter)
    && !_.isEmpty(qFilter.value.query) && _.isEqual(
      qFilter.value.query,
      combinedFilter ? combinedFilter.query : null
    );
  };

  return aggregatedRowsPlugin(processPluginConfig({
    top: {
      getRows: (items, { view }) => {
        const { rawData } = view.dataSource;

        if (!rawData ||
          !_.isArray(rawData.value) ||
          rawData.value.length <= maxRowsBeforeShowOverallTotalsOnTop) {
          return null;
        }

        let type = 'overall';

        if (!_.isEmpty(getUIFilters())) {
          if (showWaterFallFilterTotal()) {
            type = 'waterfallFilter';
          } else {
            type = 'filtered';
          }
        }

        const topConfig = _.find(rawConfig, cfg => cfg.type === type);
        if (topConfig != null) {
          const cookedConfig = cookRawConfig([topConfig], rawData, keys, i18n, helpIds);

          return [getTotalRow(rawData, cookedConfig[0])];
        }
        return null;
      },
      appendToHeader: true,
    },
    bottom: {
      getRows: (items, { view }) => {
        const { rawData } = view.dataSource;
        if (_.isUndefined(rawData)) {
          return null;
        }
        let cookedConfig = cookRawConfig(rawConfig, rawData, keys, i18n, helpIds);

        if (!showWaterFallFilterTotal()) {
          cookedConfig = _.reject(cookedConfig, config => config.type === 'waterfallFilter');
        }

        if ((filterService && _.isEmpty(getUIFilters())) || showWaterFallFilterTotal()) {
          cookedConfig = _.reject(cookedConfig, config => config.type === 'filtered');
        }

        return _.chain(cookedConfig)
          .map(config => getTotalRow(rawData, config))
          .compact()
          .value();
      },
    },
  }));
}
